// 솔루션 · 공급 기자재 데이터. 출처: 오션커넥트 회사소개자료(국문) 4장 "주력 솔루션 및 공급 기자재"
// 본문이 길어 i18n 키 대신 ko/en 객체를 함께 둡니다. (현재 언어 선택: src/lib/useLang.ts)
import type { Lang } from "@/lib/useLang";
import partnerProducts from "./partner-products.json";
import imgFirewall from "@/assets/images/solutions/firewall.jpg";
import imgEndpoint from "@/assets/images/solutions/endpoint.jpg";
import imgEndpointDashboard from "@/assets/images/solutions/endpoint-dashboard.jpg";
import imgEndpointComputers from "@/assets/images/solutions/endpoint-computers.jpg";
import imgEndpointThreats from "@/assets/images/solutions/endpoint-threats.jpg";
import imgOsDiagram from "@/assets/images/solutions/oceanseal-diagram.jpg";
import imgOsLoginKo from "@/assets/images/solutions/oceanseal-login-ko.jpg";
import imgOsLoginEn from "@/assets/images/solutions/oceanseal-login-en.jpg";
import imgOsDiagramEn from "@/assets/images/solutions/oceanseal-diagram-en.jpg";
import imgOsDashboardKo from "@/assets/images/solutions/oceanseal-dashboard-ko.jpg";
import imgOsDashboardEn from "@/assets/images/solutions/oceanseal-dashboard-en.jpg";
import imgOsShipsKo from "@/assets/images/solutions/oceanseal-ships-ko.jpg";
import imgOsShipsEn from "@/assets/images/solutions/oceanseal-ships-en.jpg";
import imgOsApproveKo from "@/assets/images/solutions/oceanseal-approve-ko.jpg";
import imgOsApproveEn from "@/assets/images/solutions/oceanseal-approve-en.jpg";
import imgOsReportKo from "@/assets/images/solutions/oceanseal-report-ko.jpg";
import imgOsReportEn from "@/assets/images/solutions/oceanseal-report-en.jpg";
import imgRmmLogin from "@/assets/images/solutions/rmm-login.jpg";
import imgRmmConsole from "@/assets/images/solutions/rmm-console.jpg";
import imgRmmTerminal from "@/assets/images/solutions/rmm-terminal.jpg";
import imgNmsLogin from "@/assets/images/solutions/nms-login.jpg";
import imgNmsDashboard from "@/assets/images/solutions/nms-dashboard.jpg";
import imgNmsPerformance from "@/assets/images/solutions/nms-performance.jpg";
import imgNmsBandwidth from "@/assets/images/solutions/nms-bandwidth.jpg";
import imgNmsTopology from "@/assets/images/solutions/nms-topology.jpg";
import imgNetAutoCover from "@/assets/images/solutions/network-automation-cover.jpg";
import imgNetAutoStatus from "@/assets/images/solutions/network-automation-status.jpg";
import imgNetAutoPolicy from "@/assets/images/solutions/network-automation-policy.jpg";
import imgCctv from "@/assets/images/solutions/cctv-marinesecu.jpg";
import imgWaterproofMonitor from "@/assets/images/solutions/waterproof-monitor.jpg";
import imgGps from "@/assets/images/solutions/gps-receiver.jpg";
import imgInmarsatPhone from "@/assets/images/solutions/inmarsat-phone.jpg";
import imgIsatphone2 from "@/assets/images/solutions/isatphone2.jpg";
import imgPmDiagram from "@/assets/images/solutions/publicmail-diagram.jpg";
import imgPmDiagramEn from "@/assets/images/solutions/publicmail-diagram-en.jpg";
import imgPmServerKo from "@/assets/images/solutions/publicmail-server-ko.jpg";
import imgPmServerEn from "@/assets/images/solutions/publicmail-server-en.jpg";
import imgPmSharedKo from "@/assets/images/solutions/publicmail-shared-ko.jpg";
import imgPmSharedEn from "@/assets/images/solutions/publicmail-shared-en.jpg";
import imgPmDevicesKo from "@/assets/images/solutions/publicmail-devices-ko.jpg";
import imgPmDevicesEn from "@/assets/images/solutions/publicmail-devices-en.jpg";
import imgVhf from "@/assets/images/solutions/vhf-recorder.jpg";
import imgInclinometer from "@/assets/images/solutions/inclinometer.jpg";
// 선박 IT 기자재: 선내 장비 랙 사진(서비스 페이지와 공용)
import imgEquipment from "@/assets/images/server-rack.jpg";

export type SolutionCategory = "security" | "remote" | "email" | "equipment";

type SolutionText = {
  title: string;
  tagline: string;
  /** 검색 결과 제목 · 설명 (생략 시 title · tagline). 목표 검색어를 앞쪽에 */
  seoTitle?: string;
  seoDescription?: string;
  intro: string; // 문단은 빈 줄("\n\n")로 나눔
  introTitle?: string; // 소개 위 제목
  highlights?: { title: string; desc: string }[]; // 소개 아래 강점 카드
  features: { title: string; desc: string }[];
  note?: string;
  specs?: { label: string; value: string }[]; // 주요 사양 표
  useCases?: { title: string; desc: string }[]; // 활용 분야
  support?: string[]; // 오션커넥트 공급·설치 지원 항목
  galleryCaptions?: string[]; // gallery 와 같은 순서
  statusBadge?: string; // comingSoon 배지 문구(생략 시 공통 문구)
  statusDesc?: string; // comingSoon 상세 안내 문구(생략 시 공통 문구)
  specsTitle?: string; // 사양 표 제목(생략 시 "주요 사양")
};

export type Solution = {
  slug: string;
  category: SolutionCategory;
  icon: string;
  image?: string; // 대표 이미지 (없으면 아이콘으로 표시)
  imageEn?: string; // 영문 페이지용 대표 이미지(글자가 들어간 그림)
  cardImage?: string; // 솔루션 목록 카드 전용 이미지(생략 시 image)
  cardImageEn?: string; // 영문 목록 카드 전용 이미지
  gallery?: string[]; // 상세 페이지 화면 갤러리
  galleryEn?: string[]; // 영문 페이지용 화면 갤러리(화면 글자가 영어인 캡처)
  comingSoon?: boolean;
  related?: string[]; // 관련 솔루션 slug (생략 시 같은 카테고리)
  text: Record<Lang, SolutionText>;
};

export const CATEGORY_LABEL: Record<SolutionCategory, Record<Lang, string>> = {
  security: { ko: "선박 사이버보안 솔루션", en: "Maritime & Ship Cyber Security Solutions" },
  remote: { ko: "원격 관리 · 모니터링", en: "Remote Management & Monitoring" },
  email: { ko: "선박 이메일 공유", en: "Vessel Email Sharing" },
  equipment: { ko: "주요 공급 시스템 · 기자재", en: "Supplied Systems & Equipment" },
};

export const SOLUTIONS: Solution[] = [
  {
    slug: "firewall",
    category: "security",
    icon: "ri-shield-check-line",
    image: imgFirewall,
    text: {
      ko: {
        title: "방화벽 (UTM)",
        tagline: "선박 네트워크 입구에서 외부 공격과 유해 트래픽을 차단합니다.",
        seoTitle: "선박 방화벽(UTM) · 네트워크 보안",
        seoDescription:
          "선박 방화벽(UTM)으로 선박 네트워크 관문에서 외부 공격과 유해 트래픽을 차단합니다. OT/IT 망분리, 육상 원격 관리, VSAT · IRIDIUM · STARLINK · LTE 다중 회선 페일오버와 위성 자동 절체를 지원합니다.",
        intro:
          "선박 방화벽(UTM)은 선박 네트워크의 관문에서 외부 공격과 유해 트래픽을 차단하는 통합 보안 장비입니다. 오랜 기간 선박 도입을 통해 안정성이 검증된 장비를 선단 단위로 일괄 구축한 실적이 있습니다.",
        features: [
          { title: "통합 보안 시스템", desc: "UTM, 애플리케이션 제어, 침입 방지 기능으로 선박 네트워크를 보호합니다." },
          { title: "네트워크 망분리", desc: "운영(OT) 네트워크와 정보(IT) 네트워크를 분리해 중요 시스템을 보호하고 안전한 운항을 돕습니다." },
          { title: "원격 관리", desc: "육상에서 로그 상태를 원격 점검하고, 느린 인터넷 환경에서도 보안 업데이트가 가능합니다." },
        ],
        note: "VSAT, IRIDIUM, STARLINK, LTE 등 여러 회선을 동시에 연결한 뒤 우선순위에 따라 페일오버 및 위성 자동 절체가 가능합니다.",
      },
      en: {
        title: "Firewall (UTM)",
        tagline: "The first line of maritime cyber security — blocks external attacks and malicious traffic at the entrance of the vessel network.",
        seoTitle: "Vessel Firewall (UTM) · Ship Network Security",
        seoDescription:
          "A vessel firewall (UTM) blocks external attacks and malicious traffic at the gateway of the ship network — with OT/IT segmentation, shore-based management, and multi-link failover and automatic satellite switching across VSAT, IRIDIUM, STARLINK and LTE.",
        intro:
          "A vessel firewall (UTM) is an integrated security appliance that protects the gateway of the ship network. We have deployed proven, ship-tested firewalls fleet-wide for our clients.",
        features: [
          { title: "Unified Security", desc: "UTM, application control, and intrusion prevention protect the vessel network." },
          { title: "Network Separation", desc: "Separates OT (operational) and IT (information) networks to protect critical systems." },
          { title: "Remote Management", desc: "Logs are checked remotely from shore, and security updates work even over slow links." },
        ],
        note: "Connects VSAT, IRIDIUM, STARLINK, LTE and other links simultaneously, with priority-based failover and automatic satellite switching.",
      },
    },
  },
  {
    slug: "endpoint",
    category: "security",
    icon: "ri-bug-line",
    image: imgEndpoint,
    gallery: [imgEndpointDashboard, imgEndpointComputers, imgEndpointThreats],
    text: {
      ko: {
        title: "엔드포인트 보안",
        tagline: "선박의 모든 IT/OT 기기를 다양한 사이버 위협으로부터 보호합니다.",
        seoTitle: "선박 엔드포인트 보안 · 백신 중앙 관리",
        seoDescription:
          "선박 PC · 서버를 랜섬웨어와 악성코드로부터 보호하는 선박 엔드포인트 보안. 육상 중앙 콘솔로 전 선단의 보안 정책을 일괄 관리하고, 선내 서버 배포로 위성 데이터 사용량을 줄입니다.",
        intro:
          "매년 증가하는 선박 사이버 해킹과 바이러스 감염에 대응하는 차세대 백신 솔루션입니다. 육상에서 전 선단의 보안 정책을 일괄 관리할 수 있습니다.",
        features: [
          { title: "랜섬웨어 · 악성코드 방어", desc: "행위 기반 탐지로 신종 위협을 실시간으로 탐지·차단하고, 감염 파일 복원을 지원합니다." },
          { title: "중앙 관리 대시보드", desc: "EPS 서버를 통해 육상에서 전 선단의 보안 정책을 일괄 적용하고 데이터 유출 방지(DLP) 기능을 제공합니다." },
          { title: "효율적인 업데이트", desc: "선내 서버가 업데이트를 수신한 뒤 내부망으로 배포하여 위성통신 비용을 절감합니다." },
        ],
        note: "PAN OCEAN 사선 전선대 · STX OCEANSERVICE 선단 안티바이러스 일괄 구축(2023)",
        galleryCaptions: [
          "육상 관리 콘솔 대시보드 — 전 선단 장치 상태 · 연결 상태 · 구성 요소 버전 한눈에",
          "선박별 컴퓨터 목록 — 선내 PC의 보안 상태, 정책, 마지막 연결 시각",
          "위협 탐지 내역 — 선박 · 장비별 탐지 유형과 대상 파일",
        ],
      },
      en: {
        title: "Endpoint Security",
        tagline: "Protects every IT/OT device on board from a wide range of cyber threats.",
        seoTitle: "Vessel Endpoint Security · Fleet Antivirus Management",
        seoDescription:
          "Endpoint security that protects vessel PCs and servers from ransomware and malware, with fleet-wide policies managed from a shore console and onboard update distribution that saves satellite bandwidth.",
        intro:
          "A next-generation antivirus solution for the growing threat of vessel hacking and malware. Security policies for the whole fleet are managed centrally from shore.",
        features: [
          { title: "Ransomware & Malware Defense", desc: "Behavior-based detection blocks new threats in real time and supports recovery of infected files." },
          { title: "Central Dashboard", desc: "Apply policies fleet-wide from shore through the EPS server, with data leak prevention (DLP)." },
          { title: "Efficient Updates", desc: "An onboard server receives updates once and distributes them internally, saving satellite bandwidth." },
        ],
        note: "Fleet-wide antivirus deployment for the entire PAN OCEAN owned fleet and STX OCEANSERVICE (2023)",
        galleryCaptions: [
          "Shore management console dashboard — fleet-wide device status, connectivity, and component versions",
          "Computers by vessel — security status, policy, and last connection of onboard PCs",
          "Threat detections — detection type and target file per vessel and device",
        ],
      },
    },
  },
  {
    slug: "usb-security",
    category: "security",
    icon: "ri-usb-line",
    image: imgOsDiagram,
    imageEn: imgOsDiagramEn,
    cardImage: imgOsLoginKo,
    cardImageEn: imgOsLoginEn,
    // 화면 소개는 개요 수준 4장만(장치 키·OS·버전·IP·최근 기록은 흐리게) - 임시 허용 코드·반출 기록 화면은 공개하지 않음
    gallery: [imgOsDashboardKo, imgOsShipsKo, imgOsApproveKo, imgOsReportKo],
    galleryEn: [imgOsDashboardEn, imgOsShipsEn, imgOsApproveEn, imgOsReportEn],
    related: ["endpoint","firewall","rmm"],
    text: {
      ko: {
        "title": "USB 보안·DLP — Ocean-Seal™",
        "tagline": "선박 PC의 USB·휴대 장치를 중앙에서 통제하고, USB로 나가는 파일을 기록·제한하며, 선급 검사(IACS UR E26)에 낼 증빙을 자동으로 남깁니다.",
        "seoTitle": "선박 USB 보안·반출 통제(DLP) — Ocean-Seal™",
        "seoDescription": "선박 PC의 USB 저장장치·휴대폰·무선랜·테더링을 중앙에서 통제하고 USB 파일 반출을 기록·제한하는 Ocean-Seal™. 서버와 끊겨도 통제가 유지되고, IACS UR E26 검사 보고서(엑셀·PDF)를 바로 만듭니다.",
        "introTitle": "선박 PC의 외부 장치 경로를 한 화면에서 관리합니다",
        "intro": "USB 메모리는 전자해도·매뉴얼 업데이트와 보고서 전달에 매일 쓰이지만, 선박 악성코드 감염의 주요 경로이기도 합니다. 선원 휴대폰 연결, 무선랜·블루투스 동글, 휴대폰 테더링처럼 선내망 밖으로 이어지는 길도 늘었습니다. 2024년 7월 1일 이후 건조 계약된 선박에 적용되는 IACS UR E26은 이런 휴대·이동식 장치의 사용 통제와 그 근거를 요구합니다.\n\nOcean-Seal™은 오션커넥트가 자체 개발한 선박용 USB·휴대 장치 통제 솔루션입니다. 선내 PC에 둔 관리 서버에서 정책을 정하면 각 PC의 에이전트가 장치를 허용·읽기 전용·차단하고, 사용·차단·반출 기록과 자산 정보를 서버로 보냅니다. 서버와 끊긴 동안에도 마지막 정책으로 계속 통제하며, 모인 기록은 선급 검사에 낼 수 있는 보고서로 바로 만듭니다.",
        "highlights": [
          {
            "title": "배 안에서 완결",
            "desc": "관리 서버를 선내 PC에 두어 인터넷이나 위성 통신 없이 동작합니다. 기록이 외부 클라우드로 나가지 않습니다."
          },
          {
            "title": "끊겨도 통제 유지",
            "desc": "PC가 서버와 끊겨도 마지막 정책으로 계속 막고, 다시 연결되면 쌓인 기록을 보냅니다. 라이선스가 끝나도 통제는 풀리지 않습니다."
          },
          {
            "title": "분리망 PC까지",
            "desc": "선내 LAN에 연결되지 않은 PC도 관리자 USB로 등록하고 정책을 바꾸며 기록을 회수합니다."
          },
          {
            "title": "검사 보고서 바로",
            "desc": "자산 목록, 허용 장치, 사용·차단 기록, 임시 허용, 경보를 IACS UR E26 검사용 엑셀·PDF로 냅니다. 내보내기 전에 고칠 점도 알려 줍니다."
          }
        ],
        "features": [
          {
            "title": "USB 저장장치를 장치마다 허용·읽기 전용·차단",
            "desc": "장치 고유 번호(VID·PID·시리얼)마다 처리를 정합니다. 처음 보는 USB는 Windows 장치 설치 단계에서 막고 승인 대기로 올라오며, 승인할 때 이 PC만·전체 PC와 만료일을 함께 정합니다."
          },
          {
            "title": "휴대폰·무선랜·블루투스·테더링 차단",
            "desc": "새 무선랜·블루투스 동글과 휴대폰 테더링, LTE 모뎀 설치를 막습니다. 휴대폰은 충전은 그대로 두고 파일 전송만 읽기 전용이나 차단으로 정하며, 관리 서버와 통신하는 어댑터는 막지 않습니다."
          },
          {
            "title": "USB 실행·CD/DVD·원격 데스크톱 통제",
            "desc": "USB·CD에서 프로그램을 바로 실행하는 것과 자동 실행을 막고, CD/DVD 굽기와 원격 데스크톱의 드라이브·클립보드·프린터 전달을 선박·PC별로 정합니다."
          },
          {
            "title": "USB 반출 통제(DLP)",
            "desc": "허용된 USB로 나간 파일의 이름·크기·SHA-256을 기록하고, 장치별 반출량 한도를 넘으면 읽기 전용이나 차단으로 바꿉니다. 문서 파일 속 주민등록번호·카드번호·여권번호를 찾아 경보하거나 격리하고, USB에서 가져온 파일과 출력 기록도 남깁니다."
          },
          {
            "title": "오프라인 임시 허용",
            "desc": "서버와 연결되지 않은 PC도 선원이 알려 준 요청 코드로 응답 코드를 만들어 정한 시간(15분~30일)만 허용합니다. 응답 코드는 그 PC·그 요청에만 맞고 한 번만 쓰이며, 발급자와 사유가 기록됩니다."
          },
          {
            "title": "자산 목록·보안 점검·변조 감지",
            "desc": "하드웨어·OS·패치·소프트웨어·네트워크·연결 장치를 자동으로 모으고 변경 이력을 남깁니다. 백신·방화벽·패치 상태를 점검하고, 에이전트 중지·파일 변조·응답 없는 PC를 경보와 웹훅(Teams·Slack 등)으로 알립니다."
          }
        ],
        "specsTitle": "지원 환경",
        "specs": [
          {
            "label": "선박 PC",
            "value": "Windows 10(2004 이상, LTSC 2021 포함) · Windows 11, 32 · 64비트"
          },
          {
            "label": "관리 서버",
            "value": "선내 Windows PC 1대, HTTPS(사설 인증서 자동 생성)"
          },
          {
            "label": "네트워크",
            "value": "선내 LAN(인터넷 · 위성 통신 불필요), 분리망 PC는 관리자 USB"
          },
          {
            "label": "언어",
            "value": "관리 화면 · 보고서 한국어 · 영어, 선원 차단 안내 10개 언어"
          },
          {
            "label": "보고서",
            "value": "검사 보고서 엑셀(전체) · PDF(요약), 자산 목록 엑셀 · PDF"
          },
          {
            "label": "E26 대응 항목",
            "value": "4.1.1 · 4.2.1 · 4.2.3 · 4.2.4 · 4.2.5 · 4.2.6 · 4.2.7"
          },
          {
            "label": "라이선스",
            "value": "PC 5 · 10 · 20 · 30대 × 1 · 2 · 3년 (평가판 30일 · PC 5대)"
          }
        ],
        "useCases": [
          {
            "title": "신조선 IACS UR E26 대응",
            "desc": "인도 전에 선박 PC의 자산 목록, 휴대 장치 통제 정책, 검사 보고서를 갖춥니다."
          },
          {
            "title": "운항 선박의 USB 악성코드 예방",
            "desc": "승인한 USB만 쓰게 하고 처음 보는 장치는 설치 단계에서 막습니다."
          },
          {
            "title": "선원 개인 장치 관리",
            "desc": "휴대폰 충전은 그대로 두고 파일 전송과 테더링을 막습니다."
          },
          {
            "title": "자료 유출 점검",
            "desc": "반출 기록과 민감 파일 검사로 개인정보와 업무 자료가 USB로 나가는지 확인합니다."
          }
        ],
        "galleryCaptions": [
          "대시보드 — 온라인 PC, 높음 경보, 승인 대기 장치, 정책 적용 현황을 한 화면에서 봅니다",
          "선박 PC — 선박 · 보안 구역별 PC 상태를 한눈에 보고, 누르면 옆 패널에 요약이 열립니다",
          "장치 승인 — 허용 · 읽기 전용 · 차단과 적용 범위(이 PC · 전체 PC), 만료일을 한 번에 정합니다",
          "검사 보고서 — 내보내기 전에 고칠 점을 보여 주고, 엑셀 · PDF로 내려받습니다"
        ]
      },
      en: {
        "title": "USB Security & DLP — Ocean-Seal™",
        "tagline": "Central control of USB and portable devices on ship PCs, with logged and limited USB file exports and automatic evidence for class surveys (IACS UR E26).",
        "seoTitle": "Maritime USB Security & DLP for Ships — Ocean-Seal™",
        "seoDescription": "Ocean-Seal™ controls USB storage, phones, Wi-Fi and tethering on ship PCs, logs and limits file exports to USB, keeps enforcing when offline and produces IACS UR E26 inspection reports in Excel and PDF.",
        "introTitle": "External device paths on ship PCs, managed from one console",
        "intro": "USB drives carry chart and manual updates and reports every day, and they are also a primary path for malware on ships. Crew phones, Wi-Fi and Bluetooth dongles and phone tethering add more ways out of the ship network. IACS UR E26, which applies to ships contracted for construction on or after 1 July 2024, requires control over such mobile and portable devices — and evidence of that control.\n\nOcean-Seal™ is a USB and portable-device control solution for ships developed in-house by Ocean Connect. You set the policy on a management server installed on an onboard PC; an agent on each PC allows, sets read-only or blocks devices and sends usage, blocking and export logs and asset information back to the server. Agents keep enforcing the last policy while disconnected, and the collected records turn straight into reports you can present at a class survey.",
        "highlights": [
          {
            "title": "Runs entirely on board",
            "desc": "The management server sits on an onboard PC and works without internet or satellite traffic. Records never leave for an external cloud."
          },
          {
            "title": "Control stays on when offline",
            "desc": "PCs keep enforcing the last policy when cut off from the server and send queued records when they reconnect. Control is not lifted when a licence ends."
          },
          {
            "title": "Air-gapped PCs included",
            "desc": "PCs that are not on the ship LAN are enrolled, updated and have their logs collected with an admin USB drive."
          },
          {
            "title": "Inspection reports on demand",
            "desc": "Asset inventory, approved devices, usage and blocking logs, temporary unlocks and alerts export to Excel and PDF for IACS UR E26 surveys, with a readiness check before you export."
          }
        ],
        "features": [
          {
            "title": "Allow, read-only or block — per USB device",
            "desc": "Set the action for each device by its unique ID (VID, PID, serial). Unknown USB drives are stopped at the Windows device-installation stage and queued for approval, where you choose this PC only or all PCs and an expiry date."
          },
          {
            "title": "Phones, Wi-Fi, Bluetooth and tethering",
            "desc": "Blocks installation of new Wi-Fi and Bluetooth dongles, phone tethering and LTE modems. Phones keep charging while file transfer is set to read-only or blocked, and the adapter used to reach the management server is never cut off."
          },
          {
            "title": "Running from USB, CD/DVD and remote desktop",
            "desc": "Stops programs running directly from USB or CD and turns off autorun, and controls CD/DVD burning and remote desktop drive, clipboard and printer redirection per ship or PC."
          },
          {
            "title": "USB data loss prevention (DLP)",
            "desc": "Logs the name, size and SHA-256 of every file copied to an allowed USB drive and switches the drive to read-only or blocked when a per-device export limit is exceeded. Documents containing national ID, card or passport numbers are flagged or quarantined, and files imported from USB and print jobs are logged too."
          },
          {
            "title": "Offline temporary unlock",
            "desc": "For a PC that cannot reach the server, turn the request code the crew reads out into a response code that allows the device for a set time (15 minutes to 30 days). Each response code fits only that PC and that request, works once, and is logged with issuer and reason."
          },
          {
            "title": "Asset inventory, security checks and tamper alerts",
            "desc": "Collects hardware, OS, patches, software, network and connected devices automatically and keeps a change history. Checks antivirus, firewall and patch status, and raises alerts and webhooks (Teams, Slack and more) when an agent is stopped, files are altered or a PC goes silent."
          }
        ],
        "specsTitle": "Supported Environment",
        "specs": [
          {
            "label": "Ship PCs",
            "value": "Windows 10 (2004 or later, incl. LTSC 2021) · Windows 11, 32/64-bit"
          },
          {
            "label": "Management server",
            "value": "One onboard Windows PC, HTTPS (private certificate created automatically)"
          },
          {
            "label": "Network",
            "value": "Ship LAN (no internet or satellite link needed); admin USB for air-gapped PCs"
          },
          {
            "label": "Languages",
            "value": "Console and reports in Korean and English; crew block notices in 10 languages"
          },
          {
            "label": "Reports",
            "value": "Inspection report in Excel (full) and PDF (summary); asset inventory in Excel and PDF"
          },
          {
            "label": "E26 clauses addressed",
            "value": "4.1.1 · 4.2.1 · 4.2.3 · 4.2.4 · 4.2.5 · 4.2.6 · 4.2.7"
          },
          {
            "label": "Licence",
            "value": "5 · 10 · 20 · 30 PCs × 1 · 2 · 3 years (30-day trial for 5 PCs)"
          }
        ],
        "useCases": [
          {
            "title": "IACS UR E26 for newbuilds",
            "desc": "Have the PC asset inventory, portable-device policy and inspection report ready before delivery."
          },
          {
            "title": "USB malware prevention in service",
            "desc": "Only approved USB drives work; unknown devices are stopped at installation."
          },
          {
            "title": "Crew personal devices",
            "desc": "Phones still charge, while file transfer and tethering are blocked."
          },
          {
            "title": "Data leak checks",
            "desc": "Export logs and sensitive-file checks show whether personal or business data leaves on USB."
          }
        ],
        "galleryCaptions": [
          "Dashboard — online PCs, high alerts, devices awaiting approval and policy rollout on one screen",
          "Ship PCs — PC status by ship and security zone at a glance, with a summary panel on click",
          "Device approval — choose allow, read-only or block, the scope (this PC or all PCs) and an expiry date in one step",
          "Inspection report — see what to fix before exporting, then download Excel or PDF"
        ]
      },
    },
  },
  {
    slug: "rmm",
    category: "remote",
    icon: "ri-remote-control-line",
    image: imgRmmLogin,
    gallery: [imgRmmConsole, imgRmmTerminal],
    text: {
      ko: {
        title: "RMM 원격관리 — OCEAN-SUPPORT™",
        tagline: "자체 플랫폼 OCEAN-SUPPORT™로 육상에서 선박 내 시스템을 안전하게 관리합니다.",
        seoTitle: "선박 원격관리 RMM 솔루션 — OCEAN-SUPPORT™",
        seoDescription:
          "선박 원격관리 플랫폼 OCEAN-SUPPORT™. 위성통신 저대역에서도 육상에서 선박 PC · 서버에 안전하게(2FA) 접속해 진단 · 조치하고, 부팅되지 않는 PC도 PE USB로 원격 복구합니다.",
        intro:
          "OCEAN-SUPPORT™는 오션커넥트가 자체 개발한 해양 원격 접속 · 지원 플랫폼(Maritime Remote Access and Support Platform)으로, 선박 원격관리를 위해 만들었습니다. 엔지니어의 방문 없이도 선박의 다양한 IT 문제를 원격으로 해결하며, 부팅조차 되지 않는 PC도 자체 개발한 PE USB를 통해 원격 복구할 수 있습니다.",
        features: [
          { title: "위성통신 저대역 환경 최적화", desc: "해상의 열악한 통신망에서도 끊김 없이 빠르고 안정적인 연결을 지원합니다." },
          { title: "2FA 인증으로 강력한 보안", desc: "이중 인증 시스템을 도입하여 선박 PC 및 주요 서버 접근 시 철저한 데이터 보안을 유지합니다." },
          { title: "비용 및 시간 절감", desc: "육상과 해상 간의 즉각적인 연결을 통해 유지보수 효율성을 극대화합니다." },
          { title: "부팅 불가 PC 원격 복구 (PE USB)", desc: "윈도우가 부팅되지 않는 선박 PC도 자체 개발한 PE USB로 부팅하면 OCEAN-SUPPORT™에 자동 연동되어, 육상에서 원격으로 진단 · 데이터 백업 · 시스템 복구를 지원합니다." },
        ],
        note: "원격 지원 14,369건 · 원격 즉시 해결률 93.5% (2019.05~2026.06)",
        galleryCaptions: ["원격 데스크톱 콘솔 — 선박 PC 화면을 육상에서 그대로 조작", "원격 터미널 — 선박 PC의 명령 프롬프트에 접속해 진단 · 조치"],
      },
      en: {
        title: "RMM Remote Management — OCEAN-SUPPORT™",
        tagline: "Securely manage onboard systems from shore with our own OCEAN-SUPPORT™ platform.",
        seoTitle: "Vessel Remote Management (RMM) — OCEAN-SUPPORT™",
        seoDescription:
          "OCEAN-SUPPORT™, our vessel remote management platform: secure (2FA) shore-to-ship access to vessel PCs and servers over low-bandwidth satellite links, and remote recovery of unbootable PCs via PE USB.",
        intro:
          "OCEAN-SUPPORT™ is the Maritime Remote Access and Support Platform developed in-house by Ocean Connect. It resolves a wide range of vessel IT issues remotely, without an engineer visit — and with our in-house PE USB, even PCs that cannot boot can be recovered remotely.",
        features: [
          { title: "Optimized for Low-bandwidth Satellite Links", desc: "Fast, stable, uninterrupted connections even over the constrained communication links at sea." },
          { title: "Strong Security with 2FA", desc: "Two-factor authentication protects access to vessel PCs and key servers, keeping data strictly secure." },
          { title: "Cost & Time Savings", desc: "Instant ship-to-shore connection maximizes maintenance efficiency." },
          { title: "Remote Recovery of Unbootable PCs (PE USB)", desc: "Even when Windows will not boot, starting the vessel PC from our in-house PE USB connects it to OCEAN-SUPPORT™ automatically, so engineers can diagnose, back up data, and restore the system remotely from shore." },
        ],
        note: "14,369 remote support cases · 93.5% immediate remote resolution (May 2019 – Jun 2026)",
        galleryCaptions: ["Remote desktop console — operate the vessel PC from shore", "Remote terminal — command-line access to the vessel PC for diagnosis and fixes"],
      },
    },
  },
  {
    slug: "nms",
    category: "remote",
    icon: "ri-dashboard-3-line",
    image: imgNmsLogin,
    gallery: [imgNmsDashboard, imgNmsPerformance, imgNmsBandwidth, imgNmsTopology],
    text: {
      ko: {
        title: "NMS 네트워크 모니터링 — Ocean-Spider™",
        tagline: "선내 사이버보안 감시 플랫폼. 위성 없이도 선박 안에서 동작합니다.",
        seoTitle: "선박 네트워크 모니터링(NMS) — Ocean-Spider™",
        seoDescription:
          "선박 네트워크 모니터링 플랫폼 Ocean-Spider™. 선내 방화벽 · 스위치 · 서버의 트래픽과 상태를 실시간 감시하고 위성 회선 대역폭 점유를 분석하며, 선급 사이버보안 규정 대응 증적을 생성합니다.",
        intro:
          "Ocean-Spider™는 오션커넥트가 자체 개발한 해양 사이버보안 감시 플랫폼(Maritime Cyber Security Platform)입니다. 선내 네트워크 장비의 트래픽과 상태를 실시간으로 감시하고 이상 징후를 경보하며, 위성 회선이 끊겨도 선박 안에서 독립적으로 동작합니다.",
        features: [
          { title: "실시간 성능 감시", desc: "방화벽 · 스위치 · 서버의 트래픽, CPU, 메모리를 SNMP 기반으로 초 단위 수집하고 그래프로 보여줍니다. 가용성과 센서 상태도 함께 감시합니다." },
          { title: "대역폭 점유 분석", desc: "비싸고 좁은 위성 회선을 누가 쓰고 있는지 장비별 · 서비스별 · 국가별로 집계합니다. 방화벽 세션 로그에서 집계하므로 OT 장비에 접촉하지 않습니다." },
          { title: "네트워크 구성도 자동 생성", desc: "방화벽 스냅샷과 세션 로그로 구성도를 자동으로 그리고, 링크 상태와 새 호스트를 추적합니다." },
          { title: "침입 탐지 · 경보 · 증적", desc: "정책 위반, 만료 인증서, 미관측 장비 등 보안 이벤트를 경보하고, 선급 사이버보안 규정 대응을 위한 증적과 제출물 체크리스트를 생성합니다." },
        ],
        galleryCaptions: [
          "대시보드 — 확인할 사항, 검사 일정 · 증적 준비도, 실시간 성능 한눈에",
          "성능 감시 — 방화벽 LAN/WAN 트래픽 실시간 그래프",
          "대역폭 점유 — 장비별 · 서비스별 위성 회선 사용량",
          "네트워크 구성도 — 방화벽 스냅샷 기반 자동 생성",
        ],
      },
      en: {
        title: "NMS Network Monitoring — Ocean-Spider™",
        tagline: "Onboard cyber security monitoring platform that works without a satellite link.",
        seoTitle: "Vessel Network Monitoring (NMS) — Ocean-Spider™",
        seoDescription:
          "Ocean-Spider™, our vessel network monitoring platform: real-time monitoring of onboard firewalls, switches and servers, satellite bandwidth usage analysis, and evidence for class cyber security rules.",
        intro:
          "Ocean-Spider™ is the Maritime Cyber Security Platform developed in-house by Ocean Connect. It provides real-time shipboard network monitoring of traffic and device status, raises alerts on anomalies, and keeps running on board even when the satellite link is down.",
        features: [
          { title: "Real-time Performance Monitoring", desc: "Collects traffic, CPU, and memory of firewalls, switches, and servers via SNMP every few seconds and graphs them, along with availability and sensor status." },
          { title: "Bandwidth Usage Analysis", desc: "Shows who is using the expensive, narrow satellite link — by device, service, and country — aggregated from firewall session logs without touching OT devices." },
          { title: "Automatic Network Topology", desc: "Draws the network map automatically from firewall snapshots and session logs, tracking link status and new hosts." },
          { title: "Intrusion Detection · Alerts · Evidence", desc: "Alerts on policy violations, expired certificates, and unobserved devices, and generates evidence and submission checklists for class cyber security rules." },
        ],
        galleryCaptions: [
          "Dashboard — items to check, inspection schedule & evidence readiness, live performance",
          "Performance monitoring — real-time firewall LAN/WAN traffic graphs",
          "Bandwidth usage — satellite link usage by device and service",
          "Network topology — generated automatically from firewall snapshots",
        ],
      },
    },
  },
  {
    slug: "network-automation",
    category: "remote",
    icon: "ri-settings-4-line",
    image: imgNetAutoCover,
    gallery: [imgNetAutoStatus, imgNetAutoPolicy],
    text: {
      ko: {
        title: "네트워크 자동화",
        tagline: "선단 전체 방화벽의 접속 상태와 정책을 육상에서 한 번에 점검하고 적용합니다.",
        seoTitle: "선박 방화벽 자동화 · 선단 네트워크 자동화",
        seoDescription:
          "선단 전체 선박 방화벽의 접속 상태 · 펌웨어 · 보안 정책을 육상에서 한 번에 점검하고 일괄 적용하는 네트워크 자동화 툴. 매일 자동 선단 조사로 변경 사항과 누락을 찾아냅니다.",
        intro:
          "오션커넥트가 자체 개발한 선단 방화벽 자동화 툴입니다. 선박마다 접속해 하나씩 설정하던 작업을 선단 단위로 자동화하여, 수백 척의 방화벽 상태 확인 · 정책 배포 · 이력 관리를 육상에서 일괄 처리합니다.",
        features: [
          { title: "선단 방화벽 현황", desc: "선박별 접속 상태 · 미접속 시간을 한 화면에서 확인하고, 기종 · 펌웨어 버전 · 미지원 펌웨어 보유 선박을 자동 집계합니다." },
          { title: "정책 일괄 적용 · 이력", desc: "보안 정책을 선단 전체에 일괄 적용하고, 성공 · 실패 · 미완료 이력을 선박별로 추적합니다. 조치가 필요한 선박은 자동으로 목록에 올라옵니다." },
          { title: "관리 접속 제한 · 출발지 제한", desc: "VIP 접근 제한과 관리 접속 허용 호스트(trusthosts)를 선단 단위로 점검 · 적용해 외부 침입 경로를 줄입니다." },
          { title: "매일 자동 선단 조사", desc: "매일 정해진 시각에 전 선박의 방화벽 설정을 수집 · 비교하여 변경 사항과 누락을 찾아내고, CSV로 내려받을 수 있습니다." },
        ],
        galleryCaptions: [
          "선단 방화벽 현황 — 선박별 접속 상태, 기종 · 펌웨어 분포, 인바운드 제한 현황",
          "정책 적용 이력 — 조치 필요 선박, 최근 21일 적용 결과, 선단 · 정책 · 접근 제한 요약",
        ],
      },
      en: {
        title: "Network Automation",
        tagline: "Check and apply firewall status and policies across the whole fleet from shore at once.",
        intro:
          "A fleet firewall automation tool developed in-house by Ocean Connect. Work that used to be done vessel by vessel is automated fleet-wide: status checks, policy rollout, and history tracking for hundreds of firewalls are handled from shore in one place.",
        features: [
          { title: "Fleet Firewall Status", desc: "See connection status and offline duration per vessel on one screen, with automatic tallies of models, firmware versions, and vessels on unsupported firmware." },
          { title: "Bulk Policy Rollout & History", desc: "Apply security policies fleet-wide and track success, failure, and pending status per vessel. Vessels needing action are listed automatically." },
          { title: "Admin & Source Restrictions", desc: "Check and enforce VIP access limits and trusted admin hosts across the fleet to shrink external attack paths." },
          { title: "Daily Automated Fleet Survey", desc: "Collects and compares every vessel's firewall configuration at a set time each day, flags changes and gaps, and exports to CSV." },
        ],
        galleryCaptions: [
          "Fleet firewall status — per-vessel connectivity, model and firmware distribution, inbound restrictions",
          "Policy history — vessels needing action, results over the last 21 days, fleet/policy/restriction summary",
        ],
      },
    },
  },
  {
    slug: "email-sharing",
    category: "email",
    icon: "ri-mail-send-line",
    image: imgPmDiagram,
    imageEn: imgPmDiagramEn,
    gallery: [imgPmServerKo, imgPmSharedKo, imgPmDevicesKo],
    galleryEn: [imgPmServerEn, imgPmSharedEn, imgPmDevicesEn],
    related: ["rmm", "nms"],
    text: {
      ko: {
        title: "이메일 공유 — OCEAN-PublicMail™",
        tagline: "선내 네트워크만으로 Exchange 메일처럼, 통신실 메일함을 선장실 · 기관실 · 사무실 PC의 Outlook에서 실시간으로 공유가 가능합니다.",
        seoTitle: "선박 Outlook 메일함 실시간 공유 — OCEAN-PublicMail™",
        seoDescription:
          "선박 통신실의 Outlook 메일함을 선장실 · 기관실 · 사무실 PC에서 Exchange 메일처럼 실시간으로 함께 씁니다. 별도 메일 서버 · 인터넷 없이 선내 LAN만으로 동기화하며 위성 통신량은 늘지 않습니다.",
        introTitle: "선내 모든 PC가 함께 쓰는 하나의 메일함",
        intro:
          "선박 메일은 보통 위성 통신을 거쳐 통신실 PC 한 대의 Outlook으로 들어옵니다. 그래서 브리지, 기관실, 사무실 등 다른 PC에서 메일을 보려면 PC마다 통신실 POP3 메일 서버 계정을 따로 설정하거나, 메일을 공유 스토리지로 옮겨 열어 보는 방식을 써 왔습니다. 하지만 이런 방식은 PC마다 설정과 관리가 따로 필요하고, 한 PC에서 받은 메일이 다른 PC에서는 보이지 않을 수 있습니다. 네트워크로 공유한 메일 파일이 손상될 위험도 있습니다.\n\nOCEAN-PublicMail™은 오션커넥트가 자체 개발한 선내 메일 공유 솔루션입니다. 통신실 메일함을 선내 다른 PC의 Outlook에 '공용 폴더'로 연결해, 각 PC에 메일 계정을 설정하지 않아도 모든 PC가 같은 메일함을 실시간으로 함께 봅니다. 육상 사무실에서 Exchange 메일을 쓰는 것과 같은 환경을 배 위에서 그대로 누릴 수 있습니다.",
        highlights: [
          { title: "지금 쓰는 메일 솔루션 그대로", desc: "GTMAIL · FLEETMAIL · BLUEWAVE 등 PST 기반 선박 메일 솔루션과 호환되므로 기존 메일 환경을 바꿀 필요가 없습니다." },
          { title: "별도 메일 서버 불필요", desc: "Exchange 같은 메일 서버 없이 선내 LAN만으로 동작합니다." },
          { title: "위성 통신량 증가 없음", desc: "공유가 선내에서만 이루어지므로 추가 위성 데이터가 발생하지 않습니다." },
          { title: "메일은 배 안에", desc: "공유되는 메일이 외부 인터넷이나 클라우드를 거치지 않습니다." },
        ],
        features: [
          { title: "Exchange처럼, 쓰던 Outlook 그대로", desc: "선내 PC마다 메일 계정을 따로 만들지 않습니다. 공유 메일함이 Outlook 폴더 창에 나타나고 읽기 · 검색 · 폴더 정리를 평소처럼 합니다. 공유 '보낼 편지함'에 넣은 메일은 통신실 Outlook이 대신 보냅니다." },
          { title: "선내 네트워크로 실시간 양방향 동기화", desc: "읽음 · 플래그 · 이동 · 삭제 · 초안 · 폴더 변경이 모든 PC의 Outlook에 곧바로 반영됩니다. 메일은 선내 LAN에서만 오가므로 위성 통신량을 늘리지 않습니다." },
          { title: "계정 · 폴더별 권한", desc: "관리 화면에서 사용자와 폴더마다 동기화 안 함 · 단방향 · 양방향과 삭제 금지를 정합니다. 볼 수 없는 폴더는 그 PC에 내려가지 않고, PC마다 계정 암호로 접속합니다." },
          { title: "대용량 메일함도 끊김 없이 안정적으로", desc: "대용량 메일함도 처음 받는 동안 새 메일을 바로 볼 수 있습니다. 서버와 연결이 끊기면 공용 폴더를 읽기 전용으로 보호했다가, 다시 연결되면 자동으로 맞춥니다. 메일함 원본은 서버가 관리해 PC가 꺼져도 메일이 사라지지 않으며, 업데이트는 서버에 한 번 적용하면 모든 PC가 자동으로 받습니다." },
        ],
        specsTitle: "지원 환경",
        specs: [
          { label: "운영체제", value: "Windows 8 · 10 · 11 (32 · 64비트)" },
          { label: "Outlook", value: "클래식 Outlook 2010~2024, Microsoft 365 (32 · 64비트)" },
          { label: "선박 메일 호환", value: "GTMAIL, FLEETMAIL, BLUEWAVE 등 PST 기반 선박 이메일 솔루션과 호환" },
          { label: "라이선스", value: "5~100대 유연한 라이선스 적용 (최대 100대 연동 가능)" },
          { label: "네트워크", value: "선내 LAN (인터넷 연결 불필요)" },
        ],
        galleryCaptions: [
          "서버 관리 화면 — 계정별로 폴더마다 메일 읽기 · 쓰기 · 삭제 권한을 정합니다",
          "공유 메일 계정 — 통신실 Outlook의 PST 메일함 중 공유할 계정을 고릅니다",
          "연결된 PC — PC마다 연결 상태와 프로그램 버전을 한눈에 봅니다",
        ],
      },
      en: {
        title: "Email Sharing — OCEAN-PublicMail™",
        tagline: "Share the radio-room mailbox in real time in Outlook on the master's, engine and office PCs over the ship's internal network — just like Exchange mail.",
        seoTitle: "Vessel Outlook Mailbox Sharing in Real Time — OCEAN-PublicMail™",
        seoDescription:
          "OCEAN-PublicMail™ shares the radio-room Outlook mailbox with the master's, engine and office PCs in real time, Exchange-style, over the ship LAN — no extra mail server, no internet connection and no extra satellite traffic.",
        introTitle: "One mailbox shared by every PC on board",
        intro:
          "Ship email usually arrives by satellite in the Outlook of a single radio-room PC. To read it on other PCs — on the bridge, in the engine room or the ship's office — crews have set up a separate account on the radio room's POP3 mail server for each PC, or moved mail to shared storage and opened it there. These approaches need separate setup and upkeep on every PC, mail downloaded on one PC may not appear on another, and mail files shared over the network risk corruption.\n\nOCEAN-PublicMail™ is an onboard mail-sharing solution developed in-house by Ocean Connect. It connects the radio-room mailbox to Outlook on other onboard PCs as a shared folder, so every PC sees the same mailbox in real time without setting up a mail account on each one — the same experience as Exchange mail in a shore office, at sea.",
        highlights: [
          { title: "Keep your ship email solution", desc: "Compatible with PST-based ship email solutions such as GTMAIL, FLEETMAIL and BLUEWAVE, so your existing mail setup stays as it is." },
          { title: "No separate mail server", desc: "Runs on the ship's LAN alone, without a mail server such as Exchange." },
          { title: "No extra satellite traffic", desc: "Sharing happens entirely on board, so no additional satellite data is used." },
          { title: "Mail stays on board", desc: "Shared mail never passes through the external internet or the cloud." },
        ],
        features: [
          { title: "Exchange-style, in the Outlook you already use", desc: "No separate mail account on each onboard PC. The shared mailbox appears in the Outlook folder pane for reading, searching and filing as usual, and mail placed in the shared Outbox is sent by the radio-room Outlook." },
          { title: "Real-time two-way sync over the ship LAN", desc: "Read status, flags, moves, deletions, drafts and folder changes appear in Outlook on every PC right away. Mail travels only on the onboard LAN, so satellite traffic does not increase." },
          { title: "Permissions per account and folder", desc: "Set no sync, one-way or two-way sync and delete protection per user and folder. Folders a user may not see are never copied to that PC, and each PC signs in with its own account password." },
          { title: "Large mailboxes, stable operation", desc: "New mail is visible right away even while a large mailbox is still being received. If the server connection drops, shared folders are protected as read-only and re-synced automatically on reconnect. The server holds the master mailbox, so no mail is lost when a PC is switched off, and an update applied once on the server reaches every PC automatically." },
        ],
        specsTitle: "Supported Environment",
        specs: [
          { label: "OS", value: "Windows 8 · 10 · 11 (32/64-bit)" },
          { label: "Outlook", value: "Classic Outlook 2010–2024, Microsoft 365 (32/64-bit)" },
          { label: "Ship email compatibility", value: "Compatible with PST-based ship email solutions such as GTMAIL, FLEETMAIL and BLUEWAVE" },
          { label: "Licence", value: "Flexible licensing from 5 to 100 PCs (up to 100 PCs connected)" },
          { label: "Network", value: "Ship LAN (no internet connection required)" },
        ],
        galleryCaptions: [
          "Server Manager — set read, write and delete rights for each folder, per account",
          "Shared mail account — choose which PST mailbox in the radio-room Outlook to share",
          "Connected PCs — see each PC's connection status and program version at a glance",
        ],
      },
    },
  },
  {
    slug: "cctv",
    category: "equipment",
    icon: "ri-video-line",
    image: imgCctv,
    text: {
      ko: {
        title: "선박용 CCTV",
        tagline: "기관실, 갑판 등 주요 구역과 사각지대를 24시간 감시합니다.",
        intro:
          "열악한 해상 환경에 맞춰 방진 · 방습 · 내식 기능을 갖춘 선박용 FULL HD 카메라(KTCC-200 시리즈)와 하이브리드 녹화기(AHVR-2204)로 구성합니다. 스테인리스 브라켓으로 선박 어느 위치에나 설치할 수 있고, 진동과 충격에 강합니다.",
        features: [
          { title: "선박용 FULL HD 카메라", desc: "HD-AHD 2MP, 3.6mm/6mm 렌즈 선택, 36개 적외선(IR) LED로 야간에도 감시합니다. 불렛형 · 하우징 일체형 · 하우징 장착형 중에서 설치 위치에 맞게 고를 수 있습니다. (KC 인증)" },
          { title: "하이브리드 녹화기", desc: "AHD · TVI · CVI · IP · SD 카메라를 한 녹화기에서 함께 사용하며, 2MP 라이브/녹화, H.265 코덱, 최대 6TB 저장, HDMI · RGB 출력을 지원합니다." },
          { title: "해양 환경 맞춤 설계", desc: "스테인리스 U형 브라켓 · 각도조절기로 진동과 충격에 강하고, 3P/4P · 동축 · UTP 등 기존 케이블을 그대로 활용할 수 있습니다." },
        ],
      },
      en: {
        title: "Marine CCTV",
        tagline: "24-hour surveillance of engine rooms, decks, and blind spots.",
        intro:
          "Marine FULL HD cameras (KTCC-200 series) with dust, moisture and corrosion resistance for harsh sea conditions, paired with a hybrid video recorder (AHVR-2204). Stainless brackets allow installation anywhere on board and withstand vibration and shock.",
        features: [
          { title: "Marine FULL HD Cameras", desc: "HD-AHD 2MP, 3.6 mm or 6 mm lens, 36 IR LEDs for night surveillance. Bullet, integrated-housing and housing-mounted types to suit each location (KC certified)." },
          { title: "Hybrid Video Recorder", desc: "Runs AHD, TVI, CVI, IP and SD cameras on one recorder, with 2MP live/recording, H.265, up to 6 TB storage and HDMI/RGB output." },
          { title: "Built for the Marine Environment", desc: "Stainless U-brackets and angle mounts resist vibration and shock; works over existing 3P/4P, coaxial or UTP cabling." },
        ],
      },
    },
  },
  {
    slug: "citadel",
    category: "equipment",
    icon: "ri-alarm-warning-line",
    text: {
      ko: {
        title: "CITADEL 비상통신",
        tagline: "해적 공격, 주 전원 마비 등 최악의 상황에서 선원의 생명과 선박을 지키는 최후의 통신 수단입니다.",
        intro:
          "GMDSS 규정을 준수하며, 주 통신 시스템과 독립된 전원과 통신망을 통해 최악의 상황에서도 외부와 음성 통화 및 데이터 통신을 보장합니다.",
        features: [
          { title: "독립 위성 통신 시스템", desc: "주 통신 시스템과 분리된 별도의 위성 안테나 및 단말기로 구성됩니다." },
          { title: "비상 전원 배터리", desc: "최소 72시간 이상 작동 가능한 배터리 시스템을 적용하였습니다." },
          { title: "호환 비상 통신 프로토콜", desc: "선박의 밀폐된 공간에서도 효과적인 구조 요청이 가능한 통신 체계를 갖추고 있습니다." },
        ],
      },
      en: {
        title: "CITADEL Emergency Communication",
        tagline: "The last line of communication to protect crew and vessel in the worst situations.",
        intro:
          "GMDSS-compliant emergency communication with power and links independent from the main system, guaranteeing voice and data contact with the outside world.",
        features: [
          { title: "Independent Satellite System", desc: "A separate satellite antenna and terminal, isolated from the main communication system." },
          { title: "Backup Battery", desc: "Battery system rated for a minimum of 72 hours of operation." },
          { title: "Compatible Emergency Protocols", desc: "Enables effective distress calls even from the vessel's sealed citadel space." },
        ],
      },
    },
  },
  {
    slug: "inclinometer",
    category: "equipment",
    icon: "ri-compass-3-line",
    image: imgInclinometer,
    text: {
      ko: {
        title: "디지털 경사계",
        tagline: "선박의 횡경사(Heel)와 종경사(Trim)를 정밀하게 측정하여 안전 운항을 지원합니다.",
        intro:
          "선박 외부 환경 변화에 대응할 수 있는 고성능 롤·피치 측정 장치입니다. 선박의 이동과 조향 동작, 화물 상태와 안정성을 쉽게 파악할 수 있습니다.",
        features: [
          { title: "고강도 · 고정밀 센서", desc: "군용 품질의 고강도·고정밀 센서로 롤링 및 피칭 값을 정확하게 측정합니다." },
          { title: "실시간 응답", desc: "적시에 응답을 확인할 수 있어 선박의 움직임을 즉각 파악합니다." },
          { title: "데이터 기록 · 재생", desc: "날짜별 경사 데이터 기록과 재생 기능으로 항해 패턴을 분석할 수 있습니다." },
        ],
      },
      en: {
        title: "Digital Inclinometer",
        tagline: "Precise measurement of heel and trim for safe navigation.",
        intro:
          "A high-performance roll and pitch measuring device that responds to changing conditions, giving a clear picture of vessel motion, steering, cargo state, and stability.",
        features: [
          { title: "Rugged, High-precision Sensor", desc: "Military-grade sensors deliver accurate rolling and pitching values." },
          { title: "Real-time Response", desc: "Immediate readings for instant awareness of vessel motion." },
          { title: "Logging & Playback", desc: "Daily inclination logs with easy playback for voyage pattern analysis." },
        ],
      },
    },
  },
  {
    slug: "waterproof-monitor",
    category: "equipment",
    icon: "ri-computer-line",
    image: imgWaterproofMonitor,
    text: {
      ko: {
        title: "해상용 방수 모니터",
        tagline: "브릿지 윙, 기관실, 갑판 등 외부에 노출된 구역에서 핵심 시스템 데이터를 실시간으로 표시합니다.",
        intro:
          "공간이 협소하거나 외부에 노출된 구역에 설치하여 CCTV, 기관실 모니터링, 항해 장비 등의 데이터를 실시간으로 표시합니다. 선박 실내는 물론 실외에도 설치할 수 있습니다.",
        features: [
          { title: "견고한 방수 디자인", desc: "IP69K 10.1인치 방수 모니터로 실외 환경에도 완벽하게 대응하며, 선바이저와 컴팩트한 디자인을 갖췄습니다." },
          { title: "유연한 설치 옵션", desc: "견고한 스테인리스 U형 브라켓으로 브릿지 윙, 기관실, 갑판 등 다양한 환경에 쉽게 설치합니다." },
          { title: "다양한 시스템 연동", desc: "CCTV 녹화기, 어군탐지기, RADAR, GPS 플로터 등 다양한 선박 장비와 연동할 수 있습니다." },
        ],
      },
      en: {
        title: "Marine Waterproof Monitor",
        tagline: "Real-time display of critical system data on bridge wings, engine rooms, and decks.",
        intro:
          "Installed in confined or exposed areas to show CCTV, engine-room monitoring, and navigation data in real time — indoors or outdoors.",
        features: [
          { title: "Rugged Waterproof Design", desc: "IP69K-rated 10.1-inch display with sun visor and compact design for outdoor use." },
          { title: "Flexible Mounting", desc: "Sturdy stainless-steel U-bracket for easy installation on bridge wings, engine rooms, and decks." },
          { title: "Wide Compatibility", desc: "Connects to CCTV recorders, fish finders, RADAR, GPS plotters, and other vessel equipment." },
        ],
      },
    },
  },
  {
    slug: "vhf-recorder",
    category: "equipment",
    icon: "ri-mic-line",
    image: imgVhf,
    text: {
      ko: {
        title: "VHF 관제 녹음장치",
        tagline: "해상교통관제(VTS) 및 선박 간 VHF 통신 내용을 자동으로 기록합니다.",
        intro:
          "VHF 통신 내용을 자동으로 기록하여 해양 사고 발생 시 중요한 법적 증거 자료로 활용하고, 선원의 통신 능력 향상을 위한 교육 자료로 사용할 수 있습니다.",
        features: [
          { title: "다중 채널 동시 녹음", desc: "최대 32채널 동시 녹음, 날짜/시간 자동 태그, 내장 백업 시스템을 갖췄습니다." },
          { title: "장기간 보존 · 원격 검색", desc: "대용량 저장소로 녹음 데이터를 장기간 보존하고, 원격 접속으로 검색·청취할 수 있습니다." },
          { title: "사고 원인 규명", desc: "고품질 음성 녹음과 손쉬운 검색·재생으로 사고 원인 규명과 해양 분쟁 해결에 결정적인 증거를 제공합니다." },
        ],
      },
      en: {
        title: "VHF Voice Recorder",
        tagline: "Automatic recording of VTS and ship-to-ship VHF communications.",
        intro:
          "Automatically records VHF communications as legal evidence in maritime incidents and as training material for crew communication skills.",
        features: [
          { title: "Multi-channel Recording", desc: "Up to 32 channels simultaneously, with automatic date/time tagging and built-in backup." },
          { title: "Long-term Storage & Remote Search", desc: "High-capacity storage with remote search and playback." },
          { title: "Incident Investigation", desc: "High-quality audio with easy search provides decisive evidence for investigations and disputes." },
        ],
      },
    },
  },
  {
    slug: "gps-receiver",
    category: "equipment",
    icon: "ri-map-pin-line",
    image: imgGps,
    text: {
      ko: {
        title: "마린 GPS 리시버",
        tagline: "고정밀 · 고이득 · 저잡음의 선박 전용 GPS 안테나 리시버입니다.",
        intro:
          "고밀도 방수 구조와 자외선에 강한 재질로 제작된 선박 전용 GPS 안테나 리시버입니다. NMEA0183 출력 프로토콜을 지원하며 다양한 항해 장비와 바로 연결할 수 있습니다.",
        features: [
          { title: "고성능 안테나 시스템", desc: "주파수 1575.42MHz, 높은 감도와 낮은 잡음으로 안정적인 신호를 수신해 정밀한 위치 확인이 가능합니다." },
          { title: "다양한 연결 옵션", desc: "기본 RS-232 외에 USB와 RS-422을 선택할 수 있으며, 10m 케이블이 포함되고 10~35V DC 전원으로 작동합니다." },
          { title: "간편한 설치", desc: "ABS 브라켓과 스틸밴드가 함께 제공되어 마스트 등 적절한 위치에 쉽게 설치할 수 있습니다." },
        ],
      },
      en: {
        title: "Marine GPS Receiver",
        tagline: "High-precision, high-gain, low-noise GPS antenna receiver built for vessels.",
        intro:
          "A marine GPS antenna receiver with a dense waterproof structure and UV-resistant materials. Supports NMEA0183 output and connects directly to navigation equipment.",
        features: [
          { title: "High-performance Antenna", desc: "1575.42 MHz with high sensitivity and low noise for stable, precise positioning." },
          { title: "Flexible Connectivity", desc: "RS-232 standard with optional USB and RS-422; 10 m cable included; 10–35 V DC power." },
          { title: "Easy Installation", desc: "ABS bracket and steel band included for simple mounting on masts or other positions." },
        ],
      },
    },
  },
  {
    slug: "inmarsat-phone",
    category: "equipment",
    icon: "ri-phone-line",
    image: imgInmarsatPhone,
    text: {
      ko: {
        title: "인마샛 위성전화기",
        tagline: "전 세계 어디서나 위성으로 음성 통화 · 문자를 주고받는 인마샛 GSPS 위성전화기입니다.",
        intro:
          "선박에 고정 설치하는 SPST-1100A와 휴대용 IsatPhone2를 공급합니다. 인마샛 GSPS 서비스는 위성이 보이는 곳이라면 전 세계 어디서나 등록된 단일 번호로 통화할 수 있어, 선박의 비상 · 백업 통신 수단으로 적합합니다.",
        features: [
          { title: "SPST-1100A 선박용 위성전화기", desc: "INMARSAT I-4 위성 GSPS 기술 기반으로 넓고 안정적인 커버리지를 제공하며 음성 통화 · SMS · 이메일을 지원합니다. 컬러 표시부로 조작이 쉬운 인마샛 승인 제품입니다." },
          { title: "IsatPhone2 휴대용 위성전화기", desc: "견고한 본체와 긴 배터리 수명, 고품질 음성을 갖췄고, 위치 정보 전송(트래킹)과 핸즈프리용 블루투스를 지원합니다." },
          { title: "비상 · 백업 통신", desc: "주 통신망 장애나 비상 상황에서도 통화할 수 있어 조난 · 긴급 통신, 원거리 항해에 대비할 수 있습니다." },
        ],
      },
      en: {
        title: "Inmarsat Satellite Phones",
        tagline: "Inmarsat GSPS satellite phones for voice and messaging anywhere in the world.",
        intro:
          "We supply the fixed-mount SPST-1100A for vessels and the handheld IsatPhone2. Inmarsat GSPS lets you call on a single registered number wherever the satellite is in view, making it a reliable emergency and backup link on board.",
        features: [
          { title: "SPST-1100A Marine Satellite Phone", desc: "Built on Inmarsat I-4 GSPS technology for wide, stable coverage, with voice, SMS and e-mail. Colour display for easy operation; Inmarsat approved." },
          { title: "IsatPhone2 Handheld Satellite Phone", desc: "Rugged body, long battery life and clear voice, with location tracking and Bluetooth for hands-free use." },
          { title: "Emergency & Backup Communication", desc: "Stay reachable when the main link fails or in an emergency, for distress, urgent calls and long voyages." },
        ],
      },
    },
  },
  {
    // 문안 · 사양은 partner-products.json
    slug: "isatphone2",
    category: "equipment",
    icon: "ri-smartphone-line",
    image: imgIsatphone2,
    text: {
      ko: { title: "휴대용 위성전화기 IsatPhone2", tagline: "", intro: "", features: [] },
      en: { title: "Handheld Satellite Phone IsatPhone2", tagline: "", intro: "", features: [] },
    },
  },
  {
    slug: "it-equipment",
    category: "equipment",
    icon: "ri-server-line",
    image: imgEquipment,
    text: {
      ko: {
        title: "선박 IT 기자재",
        tagline: "선박 운용에 필요한 모든 IT 하드웨어와 소프트웨어를 원스톱으로 공급합니다.",
        intro:
          "주요 벤더사와의 파트너십과 효율적인 구매 방식을 통해 높은 가격 경쟁력으로 선박용 IT 기자재를 공급합니다. 선용품공급업 등록 업체로 선용품도 함께 공급합니다.",
        features: [
          { title: "컴퓨팅 장비", desc: "해양 환경에 최적화된 데스크톱 PC, 워크스테이션, 서버, 선박 데이터 관리를 위한 NAS를 공급합니다." },
          { title: "네트워크 · 보안 장비", desc: "산업용 등급의 네트워크 스위치, 무선 AP, 선박 네트워크 보안을 위한 방화벽을 공급합니다." },
          { title: "주변기기 · 특수 장비", desc: "선박용 내구성 높은 프린터, 선박 데이터 수집·분석용 DAQ 장비 등을 공급합니다." },
        ],
      },
      en: {
        title: "Vessel IT Equipment",
        tagline: "One-stop supply of all IT hardware and software needed for vessel operation.",
        intro:
          "Competitive pricing through vendor partnerships and efficient procurement. As a registered ship supplier, we also provide ship supplies alongside IT equipment.",
        features: [
          { title: "Computing", desc: "Desktop PCs, workstations, servers, and NAS storage optimized for marine environments." },
          { title: "Network & Security", desc: "Industrial-grade network switches, wireless APs, and firewalls for vessel networks." },
          { title: "Peripherals & Specialty", desc: "Durable marine printers and DAQ equipment for vessel data acquisition and analysis." },
        ],
      },
    },
  },
];

// 협력사(마린시큐) 공급 제품: 상세 문안 · 사양 · 활용 분야 · 지원 내용은 partner-products.json 에서 덮어씀
for (const s of SOLUTIONS) {
  const extra = (partnerProducts as Record<string, Partial<Record<Lang, Partial<SolutionText>>>>)[s.slug];
  if (extra) for (const lang of Object.keys(s.text) as Lang[]) Object.assign(s.text[lang], extra[lang]);
}

export const findSolution =(slug: string | undefined) => SOLUTIONS.find((s) => s.slug === slug);
